import React, { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router';
import Header from './components/header';
import Footer from './components/footer';
import TermsOfUse from './pages/terms-of-use/TermsOfUse';
import VirtualAgent from './components/VirtualAgent';
import ApplicationV2 from './components/application/applicationV2';
import Quiz from './components/Quiz/Quiz';
import OrganicUsersQuiz from './components/Quiz/OrganicUsersQuiz';
import { DEFAULT_PAGE_SIZE, initialAppCriteria, initialHeaderLinks } from './shared/consts';
import { useAppConfig } from './context/app-configs-context';
import { AppActions, AppCriteria, AppResultInfo, CriteriaConstraint, CustomLink, PageInfo } from './types/decisions';
import { rankApps, rerankApps, rerankManyApps } from './api/decisions.api';
import { getCriteriaValueByType, transformCriteriaData } from './utils';
import { useAuth } from './context/auth-context';
import SessionTokenIframe from './components/SessionTokenIframe/SessionTokenIframe';
import Tka from './components/appContents/treatments/tka/Tka';
import HAInjection from './components/appContents/treatments/haInjection/HAInjection';
import OldTka from './components/appContents/treatments/tka-old/Tka';
import Loading from './components/loading/Loading';
import PhysicalTherapy from './components/appContents/treatments/physicalTherapy/PhysicalTherapy';
import Nsaids from './components/appContents/treatments/nsaids/Nsaids';
import CortisoneInjection from './components/appContents/treatments/cortisoneInjection/CortisoneInjection';
import PRP from './components/appContents/treatments/prp/PRP';
import LongTermSideEffects from './components/appContents/decisionFactors/longTermSideEffects/LongTermSideEffects';
import ShortTermSideEffects from './components/appContents/decisionFactors/shortTermSideEffects/ShortTermSideEffects';
import LongTermObjectives from './components/appContents/decisionFactors/longTermObjectives/LongTermObjectives';
import TreatmentSuccessRate from './components/appContents/decisionFactors/treatmentSuccessRate/TreatmentSuccessRate';
import TimeCommitment from './components/appContents/decisionFactors/timeCommitment/TimeCommitment';
import EffortlessRecovery from './components/appContents/decisionFactors/effortlessRecovery/EffortlessRecovery';
import LifestyleImpactOfRecovery from './components/appContents/decisionFactors/lifestyleImpactOfRecovery/LifestyleImpactOfRecovery';
import PainAvoidance from './components/appContents/decisionFactors/painAvoidance/PainAvoidance';
import NonInvasivePreference from './components/appContents/decisionFactors/nonInvasivePreference/NonInvasivePreference';
import OutOfPocketCost from './components/appContents/decisionFactors/outOfPocketCost/OutOfPocketCost';
import HomeLandingPage from './components/appContents/homeLandingPage/HomeLandingPage';
import CardiologyV2 from './pages/cardiology/CardiologyV2';
import CardiologyEnhancedV2 from './pages/cardiology/CardiologyEnhancedV2';
import Forms from './pages/forms/Forms';
import './app.scss';

const cardiologyUserIds = [ 174 ];

function App() {
    const { isAuthLoading, isAuthenticated, userInfo } = useAuth();
    const {
        appConfigs: {
            show: {
                headerMenu,
                menuItems,
                header,
                footer,
            },
            criteriaSettings: {
                zipcode,
            },
        },
    } = useAppConfig();
    const [ headerLinks, setHeaderLinks ] = useState<CustomLink[]>(initialHeaderLinks);
    const [ externalItems, setExternalItems ] = useState<CustomLink[]>([]);

    const [ resultLoading, setResultLoading ] = useState<boolean>(true);
    const initialCriteriaData = useRef<AppCriteria>(initialAppCriteria);
    const [ criteriaData, setCriteriaData ] = useState<AppCriteria>(initialAppCriteria);
    const [ criteriaConstraints, setCriteriaConstraints ] = useState<{
        [key: string]: CriteriaConstraint[]
    }>({});
    const [ results, setResults ] = useState<AppResultInfo[]>([]);
    const [ rankMode, setRankMode ] = useState<'regular' | 'multi'>('regular');
    const [ selectedProductIds, setSelectedProductIds ] = useState<AppResultInfo['id'][]>([]);
    const [ oldSelectedProductIds, setOldSelectedProductIds ] = useState<AppResultInfo['id'][]>([]);
    const [ showSelectedItems, setShowSelectedItems ] = useState(true);
    const [ pageInfo, setPageInfo ] = useState<PageInfo>({
        page_size: DEFAULT_PAGE_SIZE,
        current_page: 1,
        start: 0,
        end: 0,
        total: 0,
        total_pages: 1,
    });

    const setSelectedProductIdsWrapper = (value: SetStateAction<string[]>) => {
        setSelectedProductIds(value);
        setShowSelectedItems(true);
    };

    const onCriteriaApply = useCallback((dataset: string, options = { scrollToTop: true }) => {
        if (options.scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }

        setResultLoading(true);
        rankApps(
            transformCriteriaData(
                criteriaData,
                criteriaConstraints,
                { isZipSingleField: zipcode === 'SingleField' },
            ),
            pageInfo.page_size,
            1,
            dataset,
        )
        .then(response => {
            setRankMode('regular');
            setShowSelectedItems(true);
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setResults(response.results);
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, zipcode ]);

    const onRerank = useCallback((rerankProductId: string, dataset: string, scrollToTop: boolean = false) => {
        if (scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }
        setResultLoading(true);
        rerankApps(
            transformCriteriaData(
                criteriaData,
                criteriaConstraints,
                { isZipSingleField: zipcode === 'SingleField' },
            ),
            rerankProductId,
            pageInfo.page_size,
            1,
            dataset,
        ).then(response => {
            setResults(response.results);
            setShowSelectedItems(true);
            setRankMode('regular');
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setCriteriaData(criteriaData => (
                {
                    ...criteriaData,
                    fields: criteriaData.fields.map(field => (
                        {
                            ...field,
                            importance: response.criteria[field.id],
                        }
                    )),
                }
            ));
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
         .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, zipcode ]);

    const onRerankMany = useCallback((dataset: string, scrollToTop: boolean = false) => {
        if (scrollToTop) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: 'smooth',
            });
        }
        setResultLoading(true);
        rerankManyApps(
            transformCriteriaData(
                criteriaData,
                criteriaConstraints,
                { isZipSingleField: zipcode === 'SingleField' },
            ),
            selectedProductIds,
            pageInfo.page_size,
            1,
            dataset,
        )
        .then(response => {
            setRankMode('multi');
            setOldSelectedProductIds(selectedProductIds);
            setResults(response.results);
            setShowSelectedItems(false);
            setCriteriaData(criteriaData => (
                {
                    ...criteriaData,
                    fields: criteriaData.fields.map(field => (
                        {
                            ...field,
                            importance: response.criteria[field.id],
                        }
                    )),
                }
            ));
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, pageInfo, selectedProductIds, zipcode ]);

    const onResetTradeOffs = useCallback(() => {
        if (JSON.stringify(criteriaData.fields) === JSON.stringify(initialCriteriaData.current.fields)) {
            return;
        }

        setCriteriaData({ ...initialCriteriaData.current });
        setResultLoading(true);

        rankApps(
            transformCriteriaData(
                initialCriteriaData.current,
                criteriaConstraints,
                { isZipSingleField: zipcode === 'SingleField' },
            ),
            pageInfo.page_size,
            pageInfo.current_page,
        )
        .then(response => {
            setRankMode('regular');
            setShowSelectedItems(true);
            setSelectedProductIds([]);
            setOldSelectedProductIds([]);
            setResults(response.results);
            setPageInfo({
                current_page: response.current_page,
                start: response.start,
                end: response.end,
                total: response.total,
                total_pages: response.total_pages,
                // page_size: response.page_size,
                page_size: pageInfo.page_size,
            });
        })
        .finally(() => setResultLoading(false));
    }, [ criteriaData, criteriaConstraints, initialCriteriaData, pageInfo, zipcode ]);

    const onSliderChange = (newValue: number, id: string) => {
        setCriteriaData(criteriaData => {
            const changingFieldIdx = criteriaData.fields.findIndex(f => f.id === id);
            if (changingFieldIdx === -1) return criteriaData;
            return {
            ...criteriaData,
            fields: [
                ...criteriaData.fields.slice(0, changingFieldIdx),
                { ...criteriaData.fields[changingFieldIdx], importance: newValue },
                ...criteriaData.fields.slice(changingFieldIdx + 1),
            ],
            };
        });
    };  

    const onSliderBulkChange = (newValues: {
        [key: string]: number
    }) => {
        setCriteriaData(criteriaData => (
            {
                ...criteriaData,
                fields: criteriaData.fields.map(field => {
                    const newFieldValue = newValues[field.id];

                    if (!newFieldValue) {
                        return {
                            ...field,
                            importance: 0,
                        };
                    }

                    return {
                        ...field,
                        importance: newFieldValue,
                    };
                }),
            }
        ));
    };

    const onTradeoffValueChange = (newValue: number | string, id: string, key?: string) => {
        setCriteriaData(criteriaData => {
            const fields = criteriaData.fields;
            const idx = criteriaData.fields.findIndex(field => field.id === id);
            const newVal = getCriteriaValueByType(fields[idx], newValue, key);

            return {
                ...criteriaData,
                fields: [
                    ...fields.slice(0, idx),
                    {
                        ...fields[idx],
                        val: newVal,
                    },
                    ...fields.slice(idx + 1),
                ],
            };
        });
    };

    const onPageChange = (dataset: string, page: number) => {
        setResultLoading(true);
        if (rankMode === 'regular') {
            rankApps(
                transformCriteriaData(
                    criteriaData,
                    criteriaConstraints,
                    { isZipSingleField: zipcode === 'SingleField' },
                ),
                pageInfo.page_size,
                page,
                dataset,
            )
            .then(response => {
                setResults(response.results);
                setShowSelectedItems(true);
                setPageInfo({
                    current_page: response.current_page,
                    start: response.start,
                    end: response.end,
                    total: response.total,
                    total_pages: response.total_pages,
                    // page_size: response.page_size,
                    page_size: pageInfo.page_size,
                });
            })
            .finally(() => setResultLoading(false));
        } else {
            rerankManyApps(
                transformCriteriaData(
                    criteriaData,
                    criteriaConstraints,
                    { isZipSingleField: zipcode === 'SingleField' },
                ),
                oldSelectedProductIds,
                pageInfo.page_size,
                page,
                dataset,
            )
            .then(response => {
                setResults(response.results);
                setPageInfo({
                    current_page: response.current_page,
                    start: response.start,
                    end: response.end,
                    total: response.total,
                    total_pages: response.total_pages,
                    // page_size: response.page_size,
                    page_size: pageInfo.page_size,
                });
            })
            .finally(() => setResultLoading(false));
        }
    };

    const onActionClick = useCallback((action: AppActions) => {
        switch (action) {
            case 'DECIDE':
                return onCriteriaApply('main');
            case 'IMPORTANCE':
                return console.info('Open Importance');
            case 'SETTINGS':
                return console.info('Open SETTINGS');
            case 'RESET':
                return onResetTradeOffs();
        }
    }, [ onCriteriaApply, onResetTradeOffs ]);

    const isCardiologyUser = cardiologyUserIds.includes(+(
        userInfo?.id || '-1'
    ));

    useEffect(() => {
        setExternalItems(menuItems.externalItems);

        if (!headerMenu) {
            return;
        }

        const headerMenus = [ ...menuItems.additionalItems, ...initialHeaderLinks ];

        setHeaderLinks(headerMenus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ headerMenu, menuItems.additionalItems, menuItems.externalItems ]);

    if (isAuthLoading) {
        return (
            <>
                {!isAuthenticated && (
                    <SessionTokenIframe/>
                )}
                <Loading/>;
            </>
        );
    }

    return (
        <div className="app">
            <Router>
                {header ? (
                    <Header
                        showDecide={headerMenu && menuItems.decide}
                        showReset={headerMenu && menuItems.reset}
                        headerLinks={headerLinks}
                        externalItems={externalItems}
                        onActionClick={onActionClick}
                        shouldDisplaySteps={!isCardiologyUser}
                    />
                ) : null}
                <div className="app-content" style={{ minHeight: 'calc(100vh - 122px)' }}>
                    {isAuthenticated ? (
                        <Routes>
                            <Route path="/apps/:id" element={
                                <ApplicationV2
                                    initialCriteriaData={initialCriteriaData}
                                    criteriaData={criteriaData}
                                    criteriaConstraints={criteriaConstraints}
                                    setCriteriaConstraints={setCriteriaConstraints}
                                    setCriteriaData={setCriteriaData}
                                    onRerank={onRerank}
                                    onRerankMany={onRerankMany}
                                    onPageChange={onPageChange}
                                    pageInfo={pageInfo}
                                    onTradeoffValueChange={onTradeoffValueChange}
                                    onSliderChange={onSliderChange}
                                    results={results}
                                    resultLoading={resultLoading}
                                    setResultLoading={setResultLoading}
                                    onCriteriaApply={onCriteriaApply}
                                    onResetTradeOffs={onResetTradeOffs}
                                    onSliderBulkChange={onSliderBulkChange}
                                    setPageInfo={setPageInfo}
                                    setResults={setResults}
                                    selectedProductIds={showSelectedItems ? selectedProductIds : []}
                                    setSelectedProductIds={setSelectedProductIdsWrapper}
                                />
                            }/>
                            <Route path="/patient-choice/:id" element={
                                <ApplicationV2
                                    initialCriteriaData={initialCriteriaData}
                                    criteriaData={criteriaData}
                                    criteriaConstraints={criteriaConstraints}
                                    setCriteriaConstraints={setCriteriaConstraints}
                                    setCriteriaData={setCriteriaData}
                                    onRerank={onRerank}
                                    onRerankMany={onRerankMany}
                                    onPageChange={onPageChange}
                                    pageInfo={pageInfo}
                                    onTradeoffValueChange={onTradeoffValueChange}
                                    onSliderChange={onSliderChange}
                                    results={results}
                                    resultLoading={resultLoading}
                                    setResultLoading={setResultLoading}
                                    onCriteriaApply={onCriteriaApply}
                                    onResetTradeOffs={onResetTradeOffs}
                                    onSliderBulkChange={onSliderBulkChange}
                                    setPageInfo={setPageInfo}
                                    setResults={setResults}
                                    selectedProductIds={showSelectedItems ? selectedProductIds : []}
                                    setSelectedProductIds={setSelectedProductIdsWrapper}
                                    showSavedSettings
                                />
                            }/>
                            <Route path="/test-your-knowledge" element={<Quiz/>}/>
                            <Route path="/screening-questions" element={<OrganicUsersQuiz/>}/>
                            <Route path="/terms-of-use" element={<TermsOfUse/>}/>
                            <Route path="/virtual-agent/:id" element={<VirtualAgent/>}/>
                            {/* Treatments */}
                            <Route path="/contents/treatments/physical-therapy" element={<PhysicalTherapy/>}/>
                            <Route path="/contents/treatments/nsaids" element={<Nsaids/>}/>
                            <Route path="/contents/treatments/cortisone-injection" element={<CortisoneInjection/>}/>
                            <Route path="/contents/treatments/prp" element={<PRP/>}/>
                            <Route path="/contents/treatments/tka" element={<Tka/>}/>
                            <Route path="/contents/treatments/ha-injection" element={<HAInjection/>}/>
                            <Route
                                path="/contents/treatments"
                                element={<Navigate to="/contents/treatments/physical-therapy"/>}
                            />
                            <Route path="/old-contents/treatments/tka" element={<OldTka/>}/>
                            {/* Decision factors */}
                            <Route
                                path="/contents/decision-factors/long-term-side-effects"
                                element={<LongTermSideEffects/>}
                            />
                            <Route
                                path="/contents/decision-factors/long-term-objectives"
                                element={<LongTermObjectives/>}
                            />
                            <Route
                                path="/contents/decision-factors/treatment-success-rate"
                                element={<TreatmentSuccessRate/>}
                            />
                            <Route
                                path="/contents/decision-factors/time-commitment"
                                element={<TimeCommitment/>}
                            />
                            <Route
                                path="/contents/decision-factors/lifestyle-impact-of-recovery"
                                element={<LifestyleImpactOfRecovery/>}
                            />
                            <Route
                                path="/contents/decision-factors/effortless-recovery"
                                element={<EffortlessRecovery/>}
                            />
                            <Route
                                path="/contents/decision-factors/pain-avoidance"
                                element={<PainAvoidance/>}
                            />
                            <Route
                                path="/contents/decision-factors/short-term-side-effects"
                                element={<ShortTermSideEffects/>}
                            />
                            <Route
                                path="/contents/decision-factors/non-invasive-preference"
                                element={<NonInvasivePreference/>}
                            />
                            <Route
                                path="/contents/decision-factors/out-of-pocket-cost"
                                element={<OutOfPocketCost/>}
                            />
                            <Route
                                path="/contents/decision-factors"
                                element={<Navigate to="/contents/decision-factors/long-term-side-effects"/>}
                            />
                            <Route path="/forms/:formId" element={<Forms/>}/>
                            {isCardiologyUser ? (
                                <>
                                    <Route path="/" element={<CardiologyV2/>}/>
                                    <Route path="/cardiology-new" element={<CardiologyEnhancedV2/>}/>
                                </>
                            ) : (
                                <Route path="/" element={<HomeLandingPage/>}/>
                            )}
                            <Route path="*" element={<Navigate to="/" replace/>}/>
                        </Routes>
                    ) : (
                        <Loading/>
                    )}
                </div>
                {footer ? <Footer/> : null}
            </Router>
        </div>
    );
}

export default App;
